function stripPort(host: string): string {
    return host.replace(/:\d+$/, "").toLowerCase();
}

/**
 * Vercel preview deployments use *.vercel.app / *.vercel.sh hostnames.
 * Do not infer preview from env vars alone: that can misclassify real custom domains.
 */
export function isVercelPreviewHost(host: string): boolean {
    const normalized = stripPort(host);
    return normalized.endsWith(".vercel.app") || normalized.endsWith(".vercel.sh");
}

function isDevTunnelHost(host: string): boolean {
    const normalized = stripPort(host);
    return normalized.endsWith(".ngrok-free.dev") || normalized.endsWith(".ngrok.app") || normalized.endsWith(".ngrok-free.app");
}

/**
 * Determine host type for multi-tenant routing.
 */
/** Which surface a hostname belongs to. */
export type HostType = "crm" | "portal" | "admin" | "marketing" | "custom" | "preview";

/**
 * Classifies against the platform apex (the hostname of DW_ADMIN_LOGIN) and the portal's own
 * hostname (DW_PORTAL_LOGIN; creditportal.io in production). A portal that signs in on the
 * apex itself shares the apex, which stays marketing.
 */
export function getHostType(host: string): HostType {
    const hostWithoutPort = stripPort(host);
    const rootDomain = new URL(process.env.DW_ADMIN_LOGIN).hostname;
    const portalHostname = new URL(process.env.DW_PORTAL_LOGIN).hostname;

    if (isVercelPreviewHost(hostWithoutPort) || isDevTunnelHost(hostWithoutPort)) {
        return "preview";
    }

    if (hostWithoutPort === `app.${rootDomain}` || hostWithoutPort === "app.lvh.me" || hostWithoutPort === "app.localhost") {
        return "crm";
    }

    if ((portalHostname !== rootDomain && hostWithoutPort === portalHostname) || hostWithoutPort === "portal.lvh.me" || hostWithoutPort === "portal.localhost") {
        return "portal";
    }

    if (hostWithoutPort === `admin.${rootDomain}` || hostWithoutPort === "admin.lvh.me" || hostWithoutPort === "admin.localhost") {
        return "admin";
    }

    if (hostWithoutPort === rootDomain || hostWithoutPort === `www.${rootDomain}` || hostWithoutPort === `www.${portalHostname}` || hostWithoutPort === "localhost" || hostWithoutPort === "lvh.me" || hostWithoutPort === "127.0.0.1") {
        return "marketing";
    }

    return "custom";
}

/** Strip trailing slash; return origin (scheme + host) when input is a full URL. */
export function normalizeBaseUrl(value: string | undefined | null): string | null {
    const raw = value?.trim();
    if (!raw) {
        return null;
    }
    try {
        const url = new URL(raw);
        return `${url.protocol}//${url.host}`;
    } catch {
        return null;
    }
}

export function uniqueOrigins(...candidates: Array<string | undefined | null>): string[] {
    const seen = new Set<string>();
    for (const candidate of candidates) {
        const normalized = normalizeBaseUrl(candidate);
        if (normalized) {
            seen.add(normalized);
        }
    }
    return [...seen];
}

export function rootDomainFromHostname(hostname: string): string {
    const host = hostname.replace(/:\d+$/, "").toLowerCase();
    if (host === "localhost" || host === "127.0.0.1" || host === "::1") {
        return "localhost";
    }
    if (host === "lvh.me" || host.endsWith(".lvh.me")) {
        return "lvh.me";
    }

    const labels = host.split(".");
    const first = labels[0];
    if (labels.length >= 3 && (first === "app" || first === "portal" || first === "admin" || first === "onboarding")) {
        return labels.slice(1).join(".");
    }
    if (host.startsWith("www.")) {
        return host.slice(4);
    }
    return host;
}

export function isLocalRootDomain(rootDomain: string): boolean {
    const root = rootDomain.toLowerCase();
    return root === "localhost" || root === "lvh.me";
}

function isPrivateIpv4Host(host: string): boolean {
    const parts = host.split(".");
    if (parts.length !== 4) {
        return false;
    }

    const numbers = parts.map((part) => {
        if (part.length === 0) {
            return Number.NaN;
        }
        const value = Number(part);
        if (!Number.isInteger(value) || value < 0 || value > 255 || String(value) !== part) {
            return Number.NaN;
        }
        return value;
    });

    if (numbers.some((value) => Number.isNaN(value))) {
        return false;
    }

    const [a, b] = numbers;
    return a === 10 || a === 127 || a === 192 && b === 168 || a === 172 && b >= 16 && b <= 31;
}

export function isLocalOrPrivateHost(host: string): boolean {
    return host === "localhost"
        || host === "0.0.0.0"
        || host === "::1"
        || host === "lvh.me"
        || host.endsWith(".lvh.me")
        || isPrivateIpv4Host(host);
}
